import { type ClientBoardSource as ClientBoardSourceType, type ClientBoardSource } from '@gorilla/portal-shared/src/schemas';
import { Person, MoveArrowRight, BoardPrivate, Idea } from '@vibe/icons';
import { Box, Flex, Text, Icon, Divider } from '@vibe/core';
import { useSingleBoard } from '../../shared/hooks/use-boards';
import { mondayClient } from '../../services/monday';
import { getBoards } from '@gorilla/common/src/lib/monday-api/api';
import { extractBoardNameAndErrorMessage, BoardSourceExplainer, EditBoardField, EditBoardSourceNameField, BoardSourceId } from './common';
import { ExternalLink } from '../ExternalLink';

type ClientBoardSourceProps = {
  source: Partial<ClientBoardSourceType>;
  onChange: (source: Partial<ClientBoardSourceType>) => void;
  mode: 'create' | 'edit';
};

export function ClientBoardSource({ source, onChange }: ClientBoardSourceProps) {
  const singleBoardResult = useSingleBoard(source.templateBoardId);
  const { boardName, boardErrorMessage } = extractBoardNameAndErrorMessage(singleBoardResult, source.templateBoardId);

  return (
    <Box
      rounded="small"
      border={true}
      borderColor="uiBorderColor"
      style={{
        margin: '0 0 24px 0',
      }}
    >
      <Box>
        <BoardSourceId
          sourceId={source.id || ''}
          headline="Identifier"
          helpText="This ID is only relevant when referencing this setup in workflow automations."
        />
        <Divider withoutMargin />
        <EditBoardField
          isCompleted={!!source.templateBoardId}
          headline="Template board"
          helpText="This defines the column structure for all dedicated client boards."
          introText="Choose the template board"
          value={boardName}
          errorMessage={boardErrorMessage}
          onChange={(newSelectedBoardId) => {
            onChange({ ...source, templateBoardId: newSelectedBoardId });
          }}
        />
        <Divider withoutMargin />
        <EditBoardSourceNameField
          headline="Name"
          helpText="This helps you identify it when assigning dedicated boards."
          introText="Give this setup a unique name"
          value={source.name || ''}
          isCompleted={!!source.name}
          boardSourceName={source.name || ''}
          onChange={(newBoardSourceName) => {
            onChange({ ...source, name: newBoardSourceName });
          }}
        />
      </Box>
    </Box>
  );
}

export function ClientBoardSourceExplainer() {
  return (
    <BoardSourceExplainer
      title="Clients see their own board in the client portal"
      description="Each client will see their own dedicated board, created from a single template."
      videoSrc="/Onboarding-Dedicated-Board-Compressed.mp4"
    />
  );
}

export function ClientBoardSourceIndicator({
  children,
  helpLink,
  hideHelpLink,
}: {
  children: React.ReactNode;
  helpLink?: string;
  hideHelpLink?: boolean;
}) {
  return (
    <Flex gap={2}>
      <Flex gap={2}>
        <Icon icon={BoardPrivate} size={18} />
        <Icon icon={MoveArrowRight} size={12} />
        <Icon icon={Person} size={16} />
      </Flex>
      <div style={{ marginRight: '8px', marginLeft: '8px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {children}
      </div>
      {helpLink && !hideHelpLink ? (
        <div>
          <ExternalLink href={helpLink} style={{ color: '#1f76c2' }}>
            Need help?
          </ExternalLink>
        </div>
      ) : null}
    </Flex>
  );
}
